import { SELECTED_ACCOUNT_CHAIN_ID, nonzeroAddressSchema, type Address, type Hex, type PreparedOperation } from '@hoodmorning/contracts';
import { encodeFunctionData, getAddress, isAddressEqual, parseAbi, verifyTypedData } from 'viem';
import { hashPreparedCalls } from './calls.js';
import { validateOperationForSimulation } from './batchSimulation.js';

export const ownerRotationAbi = parseAbi(['function updateFallbackSignerData(address fallbackSigner,bool isDisabled)']);

export type OwnerAcceptanceMessage<ChainId extends 4663 | 46630 = 4663> = {
  chainId: ChainId;
  vault: Address;
  currentOwner: Address;
  newOwner: Address;
  nonce: Hex;
  expiresAt: bigint;
};

export type OwnerAcceptanceProof<ChainId extends 4663 | 46630 = 4663> = OwnerAcceptanceMessage<ChainId> & {
  signature: Hex;
};

export const ownerAcceptanceTypes = {
  OwnerAcceptance: [
    { name: 'vault', type: 'address' },
    { name: 'currentOwner', type: 'address' },
    { name: 'newOwner', type: 'address' },
    { name: 'nonce', type: 'bytes32' },
    { name: 'expiresAt', type: 'uint48' },
  ],
} as const;

export function ownerAcceptanceTypedData<ChainId extends 4663 | 46630>(message: OwnerAcceptanceMessage<ChainId>) {
  for (const address of [message.vault, message.currentOwner, message.newOwner]) nonzeroAddressSchema.parse(address);
  if (message.chainId !== SELECTED_ACCOUNT_CHAIN_ID) throw new Error(`Owner acceptance is restricted to chain ${SELECTED_ACCOUNT_CHAIN_ID}`);
  if (isAddressEqual(message.vault, message.currentOwner)) throw new Error('Vault must be distinct from owner');
  if (isAddressEqual(message.currentOwner, message.newOwner)) throw new Error('New owner must differ');
  if (isAddressEqual(message.vault, message.newOwner)) throw new Error('Vault cannot own itself');
  if (!/^0x[0-9a-fA-F]{64}$/.test(message.nonce)) throw new Error('Nonce must be bytes32');
  if (message.expiresAt <= 0n || message.expiresAt >= 1n << 48n) {
    throw new RangeError('expiresAt must be a positive uint48');
  }

  return {
    domain: {
      name: 'HoodMorning Owner Acceptance',
      version: '1',
      chainId: message.chainId,
      verifyingContract: getAddress(message.vault),
    },
    types: ownerAcceptanceTypes,
    primaryType: 'OwnerAcceptance' as const,
    message: {
      vault: getAddress(message.vault),
      currentOwner: getAddress(message.currentOwner),
      newOwner: getAddress(message.newOwner),
      nonce: message.nonce,
      expiresAt: Number(message.expiresAt),
    },
  };
}

export async function verifyOwnerAcceptance<ChainId extends 4663 | 46630>(
  proof: OwnerAcceptanceProof<ChainId>,
  expected: Omit<OwnerAcceptanceMessage<ChainId>, 'nonce' | 'expiresAt'>,
  nowUnixSeconds: bigint,
): Promise<{ valid: boolean; failures: string[] }> {
  const failures: string[] = [];
  if (proof.chainId !== expected.chainId) failures.push('CHAIN_MISMATCH');
  if (!isAddressEqual(proof.vault, expected.vault)) failures.push('VAULT_MISMATCH');
  if (!isAddressEqual(proof.currentOwner, expected.currentOwner)) failures.push('CURRENT_OWNER_MISMATCH');
  if (!isAddressEqual(proof.newOwner, expected.newOwner)) failures.push('NEW_OWNER_MISMATCH');
  if (proof.expiresAt <= nowUnixSeconds) failures.push('PROOF_EXPIRED');

  try {
    ownerAcceptanceTypedData(proof);
  } catch {
    failures.push('PROOF_SHAPE_INVALID');
  }

  if (failures.length === 0) {
    try {
      const validSignature = await verifyTypedData({
        address: proof.newOwner,
        ...ownerAcceptanceTypedData(proof),
        signature: proof.signature,
      });
      if (!validSignature) failures.push('NEW_OWNER_SIGNATURE_INVALID');
    } catch {
      failures.push('NEW_OWNER_SIGNATURE_INVALID');
    }
  }

  return { valid: failures.length === 0, failures };
}

/** Prepares a vault self-call, never an owner-address/7702 call. The acceptance is an
 * off-chain application check: the pinned account does not consume its nonce or expiry.
 * Current-owner on-chain preflight, exact simulation and owner authorization are still required.
 */
export async function prepareOwnerRotation<ChainId extends 4663 | 46630>(input: {
  proof: OwnerAcceptanceProof<ChainId>;
  expected: Omit<OwnerAcceptanceMessage<ChainId>, 'nonce' | 'expiresAt'>;
  operationId: string;
  nonceKey: bigint;
  now?: () => Date;
}): Promise<PreparedOperation> {
  const proof = { ...input.proof };
  const expected = { ...input.expected };
  const { operationId, nonceKey } = input;
  const now = input.now ?? (() => new Date());
  const startedAt = now();
  if (!Number.isFinite(startedAt.getTime())) throw new Error('Invalid rotation time');
  const verification = await verifyOwnerAcceptance(proof, expected, BigInt(Math.floor(startedAt.getTime() / 1000)));
  if (!verification.valid) throw new Error(`Owner acceptance rejected: ${verification.failures.join(',')}`);
  const calls = [{ to: getAddress(expected.vault), value: 0n, data: encodeFunctionData({ abi: ownerRotationAbi, functionName: 'updateFallbackSignerData', args: [getAddress(expected.newOwner), false] }) }];
  const expiry = Math.min(Number(proof.expiresAt) * 1000, startedAt.getTime() + 300_000);
  const operation = { operationId, vault: getAddress(expected.vault), calls, callsHash: hashPreparedCalls(calls), nonceKey, validUntil: new Date(expiry).toISOString() };
  validateOperationForSimulation(operation, now());
  return operation;
}
